"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setFollowSystem: (follow: boolean) => void;
}

// Historically this app persisted the theme via a third-party store
// library's persistence middleware, which wrote the envelope
// `{"state":{"theme":"..."},"version":0}` under this key. This plain-context
// implementation instead writes the bare string value ("dark" | "light").
// `readStoredTheme` accepts both shapes so an existing user's chosen theme
// survives the migration; new writes always use the plain-string format.
const THEME_STORAGE_KEY = "theme-storage";

const ThemeContext = createContext<ThemeContextValue | null>(null);

const getSystemTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const applyThemeToDOM = (theme: Theme) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
};

const isTheme = (value: unknown): value is Theme =>
  value === "dark" || value === "light";

const readStoredTheme = (): Theme | null => {
  if (typeof window === "undefined") return null;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    // Legacy persisted envelope: {"state":{"theme":"dark"},"version":0}
    if (parsed && typeof parsed === "object" && "state" in parsed) {
      const legacyTheme = (parsed as { state?: { theme?: unknown } }).state
        ?.theme;
      return isTheme(legacyTheme) ? legacyTheme : null;
    }

    // A JSON-encoded plain string, e.g. `"dark"`.
    return isTheme(parsed) ? parsed : null;
  } catch {
    // Not JSON — treat the raw value as the bare plain-string format.
    return isTheme(raw) ? raw : null;
  }
};

const writeStoredTheme = (theme: Theme) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // The selected theme still works when browser storage is unavailable.
  }
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>("dark");
  const [followSystem, setFollowSystem] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.dataset.themeMode === "system",
  );

  useEffect(() => {
    const initialTheme = followSystem
      ? getSystemTheme()
      : (readStoredTheme() ?? getSystemTheme());
    setTheme(initialTheme);
    applyThemeToDOM(initialTheme);
  }, [followSystem]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    let lastSystemTheme = getSystemTheme();

    const handleChange = (event: MediaQueryListEvent) => {
      const newSystemTheme: Theme = event.matches ? "dark" : "light";
      if (followSystem) {
        setTheme(newSystemTheme);
        applyThemeToDOM(newSystemTheme);
        return;
      }
      setTheme((currentTheme) => {
        // Only follow system changes if the user hasn't explicitly chosen a
        // theme since the last system-level change.
        if (currentTheme === lastSystemTheme) {
          applyThemeToDOM(newSystemTheme);
          writeStoredTheme(newSystemTheme);
          lastSystemTheme = newSystemTheme;
          return newSystemTheme;
        }
        lastSystemTheme = newSystemTheme;
        return currentTheme;
      });
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [followSystem]);

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) => {
      const newTheme: Theme = currentTheme === "dark" ? "light" : "dark";
      applyThemeToDOM(newTheme);
      writeStoredTheme(newTheme);
      return newTheme;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setFollowSystem }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (context) {
    return context;
  }

  // Consumers rendered outside <ThemeProvider> (e.g. before it mounts, or in
  // isolated tests) fall back to the system preference, mirroring the
  // previous store-backed default.
  return {
    theme: getSystemTheme(),
    toggleTheme: () => {},
    setFollowSystem: () => {},
  };
};
